import { useId, useRef, useEffect } from 'react';
import type { DropdownType, DropdownItemType } from './types';
import { Button, IconMoreVertical } from 'tvs-unified-lib';
import styled from 'styled-components';

type ButtonType = {
  variant?: 'block' | 'outline' | 'no-border';
  theme?: 'purple' | 'blue' | 'red' | 'green' | 'grey' | 'yellow';
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  iconOnly?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  type?: 'button' | 'submit' | 'reset' | undefined;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

type DropdownButtonType = {
  // set unique id to each dropdown, for determining which dropdown open and closes if multiple instance exists on a page
  id: string;
  buttonProps?: ButtonType;
} & DropdownType;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 1000;
  display: none;

  &.theme-dropdown-animation {
    animation: dropdownFadeIn 0.2s ease-in-out;
  }

  @keyframes dropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DropdownItem = styled.div<{ disabled?: boolean }>`
  padding: 8px 16px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  color: ${props => props.disabled ? '#999' : '#333'};
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.disabled ? 'transparent' : '#f5f5f5'};
  }
`;

const DropdownButton = (props: DropdownButtonType) => {
  const {
    id,
    containerClassName,
    menus,
    closeOtherDropdownOnOpen = true,
    buttonProps,
  } = props;
  const componentId = useId();
  // const buttonRef = useRef<HTMLButtonElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Element)) {
      if (dropdownRef.current) {
        dropdownRef.current.style.display = 'none';
      }
    }
  };

  const handleButtonClick = () => {
    if (dropdownRef.current) {
      if (
        dropdownRef.current.style.display === undefined ||
        dropdownRef.current.style.display === 'none'
      ) {
        dropdownRef.current.style.display = 'block';
        dropdownRef.current.classList.add('theme-dropdown-animation');
      } else {
        dropdownRef.current.style.display = 'none';
      }
    }

    if (closeOtherDropdownOnOpen) {
      const allDropdowns = document.querySelectorAll(
        `.theme-dropdown:not(#theme-dropdown-${id})`,
      );
      allDropdowns.forEach(dropdown => {
        (dropdown as HTMLElement).style.display = 'none';
        (dropdown as HTMLElement).classList.remove('theme-dropdown-animation');
      });
    }
  };

  useEffect(() => {
    if (dropdownRef.current) {
      dropdownRef.current.style.display = 'none';
    }
  }, [dropdownRef]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <DropdownContainer ref={ref} className={containerClassName}>
      <Button
        // ref={buttonRef}
        {...buttonProps}
        label={buttonProps?.label ?? ''}
        icon={
          buttonProps?.icon ?? (
            <IconMoreVertical className='fill-color-purple-500' />
          )
        }
        onClick={handleButtonClick}
      />
      <DropdownMenu
        id={`theme-dropdown-${id}`}
        ref={dropdownRef}
      >
        {menus.map((menu: DropdownItemType, index: number) => (
          <DropdownItem
            key={`${componentId}-dropdown-${index}`}
            disabled={menu.disabled}
            onClick={() => {
              if (!menu.disabled) {
                menu.onClick();
              }
            }}
            className={menu.className}
          >
            {menu.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </DropdownContainer>
  );
};

export default DropdownButton;
